import {Action, Module, Mutation, VuexModule} from 'vuex-module-decorators';
import {Currency} from "@/enums/Currency";
import {OrderItem} from "@/util/OrderItem";
import {Response} from "@/util/Response";
import {Store} from "@/enums/Store";
import {Member} from "@/util/Member";
import {User} from "@/util/User";
import {Location} from "@/util/Location";


@Module({
  namespaced: true
})
export default class Cart extends VuexModule {
  storeId?: number = undefined;
  currency?: Currency = undefined;
  orderId?: number | string = undefined;
  showDiscountResetButton = true

  @Mutation
  SET_STORE_ID(storeId: number) {
    this.storeId = storeId;
  }

  @Mutation
  SET_SHOW_REBATE_RESET_BUTTON(value: boolean) {
    this.showDiscountResetButton = value
  }

  @Mutation
  SET_CURRENCY(currency: Currency) {
    this.currency = currency;
  }

  @Mutation
  SET_ORDER_ID(orderId: string | number) {
    this.orderId = orderId;
  }

  @Action
  init({ storeId, currency }: InitPayload) {
    const { commit } = this.context;

    commit('SET_STORE_ID', storeId);
    commit('SET_CURRENCY', currency);
  }

  @Action
  setShowDiscountResetButton(value: boolean) {
    const {commit} = this.context;

    commit('SET_SHOW_REBATE_RESET_BUTTON', value);
  }

  get orderItems() {
    const { rootGetters, rootState } = this.context;

    const event = rootGetters[`${Store.EVENT}/selectedEventObject`];
    const members = rootState[Store.MEMBER].members;

    if (members) {
      const orderItems: OrderItem[] = [];

      for (const member of members) {
        const orderItem = OrderItem.create(member, event, 1);
        if (orderItem) {
          orderItems.push(orderItem);
        }
      }
      return orderItems;
    }
    return undefined;
  }

  get orderItem() {
    const { rootGetters } = this.context;
    return (member: Member) => {
      const event = rootGetters[`${Store.EVENT}/selectedEventObject`];

      return OrderItem.create(member, event, 1);
    };
  }

  get qty(): number | undefined {
    const { getters } = this.context;

    if (getters.orderItems) {
      const qty = getters.orderItems.reduce((qty: number, orderItem: OrderItem) => {
        return qty + orderItem.qty;
      }, 0);

      return qty;
    }
    return undefined;
  }

  get total(): number | undefined {
    const { getters } = this.context;

    if (getters.orderItems) {
      const total = getters.orderItems.reduce((total: number, orderItem: OrderItem) => {
        return total + orderItem.qty * orderItem.price;
      }, 0);

      return total;
    }
    return undefined;
  }

  get response(): Response | undefined {
    if (this.storeId == undefined || this.currency == undefined) {
      return undefined;
    }

    const { getters, rootState } = this.context;

    const location: Location = rootState[Store.LOCATION].location;
    const locationCode: string = rootState[Store.LOCATION].locationCode;
    const members: Member[] = rootState[Store.MEMBER].members;
    const invoiceRecipient: User = rootState[Store.INVOICE_RECIPIENT].invoiceRecipient;
    const customer: User = rootState[Store.CUSTOMER].customer;

    const response = new Response({
      storeId: this.storeId,
      locationId: location.entityId,
      locationCode: locationCode,
      currency: this.currency,
      createdAt: new Date(),
      orderItems: getters.orderItems,
      qty: getters.qty,
      total: getters.total,
      memberList: members,
      customer,
      invoiceRecipient
    });

    return response;
  }
}

export interface InitPayload {
  storeId: number;
  currency: Currency;
}
